import { Controller } from '@hotwired/stimulus'

/**
 * @typedef {Object} SwiperModules
 * @property {import('swiper').default} Swiper — конструктор Swiper.
 * @property {unknown} Navigation — модуль навигации Swiper.
 * @property {unknown} Thumbs — модуль связи основного слайдера с миниатюрами.
 * @property {unknown} EffectFade — модуль fade-перехода между слайдами.
 * @property {unknown} Mousewheel — модуль прокрутки миниатюр колесом мыши.
 * @property {unknown} FreeMode — модуль свободной прокрутки миниатюр.
 * @property {unknown} Scrollbar — модуль скроллбара для миниатюр.
 */

/**
 * Stimulus-контроллер для контентной галереи на Swiper.
 *
 * Лениво загружает Swiper, создает основной fade-слайдер и горизонтальную
 * ленту миниатюр, связывает миниатюры с основными слайдами и скрывает контролы,
 * если в галерее только одно изображение.
 *
 * @extends {Controller}
 */
export default class ContentGalleryController extends Controller {
  /**
   * DOM-цели контроллера.
   *
   * `slides` — корневой элемент основного Swiper, `prevBtn` и `nextBtn` —
   * кнопки переключения между слайдами.
   *
   * @type {string[]}
   */
  static targets = ['slides', 'nextBtn', 'prevBtn']

  /**
   * Активный инстанс основного Swiper.
   *
   * @type {import('swiper').default | null}
   */
  #swiper = null

  /**
   * Активный инстанс Swiper для миниатюр.
   *
   * @type {import('swiper').default | null}
   */
  #thumbsSwiper = null

  /**
   * DOM-элемент Swiper-контейнера миниатюр.
   *
   * @type {HTMLElement | null}
   */
  #thumbsContainer = null

  /**
   * Внешняя обертка контейнера миниатюр, которую контроллер создает сам.
   *
   * @type {HTMLElement | null}
   */
  #thumbsContainerWrapper = null

  /**
   * Идентификатор последней попытки инициализации.
   *
   * Нужен, чтобы игнорировать результат устаревшего асинхронного импорта после
   * Turbo-перехода или повторного подключения контроллера.
   *
   * @type {number}
   */
  #initRunId = 0

  /**
   * Кеш результата динамического импорта Swiper и его модулей.
   *
   * @type {SwiperModules | null}
   */
  static #swiperCache = null

  /**
   * Запускает асинхронную инициализацию галереи при подключении контроллера.
   */
  connect() {
    this.#initRunId += 1
    this.#init(this.#initRunId)
  }

  /**
   * Отменяет ожидающую инициализацию, уничтожает Swiper-инстансы и удаляет
   * созданную контроллером разметку миниатюр.
   */
  disconnect() {
    this.#initRunId += 1
    this.#swiper?.destroy(true, true)
    this.#thumbsSwiper?.destroy(true, true)
    this.#swiper = null
    this.#thumbsSwiper = null

    if (this.#thumbsContainerWrapper) {
      this.#thumbsContainerWrapper.remove()
      this.#thumbsContainerWrapper = null
      this.#thumbsContainer = null
    }
  }

  /**
   * Создает Swiper-инстансы для основной галереи и миниатюр.
   *
   * При одном изображении скрывает контролы и не инициализирует Swiper, чтобы
   * не показывать бессмысленную навигацию и миниатюры.
   *
   * @param {number} runId — идентификатор текущей попытки инициализации.
   * @returns {Promise<void>}
   */
  async #init(runId) {
    if (this.#slidesCount <= 1) {
      this.#setControlsVisible(false)
      return
    }

    this.#setControlsVisible(true)

    try {
      const {
        Swiper,
        Navigation,
        Thumbs,
        EffectFade,
        Mousewheel,
        FreeMode,
        Scrollbar
      } = await this.#importSwiper()

      if (!this.element.isConnected || runId !== this.#initRunId) return

      this.#createThumbs()
      if (this.#thumbsContainer) {
        this.#thumbsSwiper = new Swiper(this.#thumbsContainer, {
          modules: [Mousewheel, FreeMode, Scrollbar],
          speed: 400,
          slidesPerView: 'auto',
          spaceBetween: 6,
          freeMode: { enabled: true, momentumBounce: true },
          mousewheel: { sensitivity: 1, releaseOnEdges: true },
          breakpoints: {
            1024: { scrollbar: { draggable: true } }
          }
        })
      }

      if (this.hasSlidesTarget) {
        this.#swiper = new Swiper(this.slidesTarget, {
          modules: [Navigation, Thumbs, EffectFade],
          speed: 800,
          slidesPerView: 1,
          allowTouchMove: true,
          loop: true,
          loopPreventsSliding: false,
          effect: 'fade',
          fadeEffect: {
            crossFade: true
          },
          grabCursor: true,
          navigation: {
            nextEl: this.hasNextBtnTarget ? this.nextBtnTarget : null,
            prevEl: this.hasPrevBtnTarget ? this.prevBtnTarget : null
          },
          thumbs: { swiper: this.#thumbsSwiper, autoScrollOffset: 1 },
          on: {
            afterInit: (swiper) => {
              setTimeout(() => {
                if (!this.element.isConnected || runId !== this.#initRunId) {
                  return
                }

                swiper.slides.forEach((slide) => {
                  slide.style.transitionProperty = 'opacity'
                  slide.style.transitionDuration = `${swiper.params.speed}ms`
                  slide.style.transitionTimingFunction = 'ease-in-out'
                })
              }, swiper.params.speed)
            }
          }
        })
      }
    } catch (error) {
      if (!this.element.isConnected || runId !== this.#initRunId) return
      console.error('Failed to initialize content gallery swiper', error)
    }
  }

  /**
   * Количество исходных слайдов в разметке без учета loop-клонов Swiper.
   *
   * @returns {number}
   */
  get #slidesCount() {
    if (!this.hasSlidesTarget) return 0

    return this.slidesTarget.querySelectorAll('.swiper-wrapper > .swiper-slide')
      .length
  }

  /**
   * Показывает или скрывает навигационные контролы галереи.
   *
   * Если в разметке есть общая обертка `.content-gallery__nav`, скрывает ее;
   * иначе скрывает отдельные кнопки.
   *
   * @param {boolean} visible — нужно ли показывать контролы.
   */
  #setControlsVisible(visible) {
    const display = visible ? '' : 'none'
    const nav = this.element.querySelector('.content-gallery__nav')

    if (nav) {
      nav.style.display = display
    } else {
      if (this.hasPrevBtnTarget) this.prevBtnTarget.style.display = display
      if (this.hasNextBtnTarget) this.nextBtnTarget.style.display = display
    }
  }

  /**
   * Создает Swiper-разметку миниатюр на основе изображений из основных слайдов.
   *
   * Миниатюры добавляются динамически, чтобы не дублировать галерейную разметку
   * во view и всегда держать порядок превью синхронным с основным слайдером.
   */
  #createThumbs() {
    if (!this.#thumbsContainer) {
      this.#thumbsContainerWrapper = document.createElement('div')
      this.#thumbsContainerWrapper.classList.add(
        'content-gallery__thumbs-wrapper'
      )
      this.#thumbsContainer = document.createElement('div')
      this.#thumbsContainer.classList.add('swiper-thumbnails', 'swiper')

      const thumbsWrapper = document.createElement('div')
      thumbsWrapper.classList.add('swiper-wrapper')
      this.#thumbsContainer.appendChild(thumbsWrapper)

      this.slidesTarget.querySelectorAll('.swiper-slide').forEach((slide) => {
        const thumb = document.createElement('div')
        thumb.classList.add('swiper-thumbnail', 'swiper-slide')

        const image = slide.querySelector('img')
        if (image) {
          const thumbImage = document.createElement('img')
          thumbImage.src = image.src
          thumbImage.alt = image.alt
          thumbImage.width = 94
          thumbImage.height = 54
          thumb.appendChild(thumbImage)
        }

        thumbsWrapper.appendChild(thumb)
      })

      this.#thumbsContainerWrapper.appendChild(this.#thumbsContainer)
      this.element.appendChild(this.#thumbsContainerWrapper)
    }
  }

  /**
   * Загружает Swiper и необходимые модули один раз на страницу.
   *
   * @returns {Promise<SwiperModules>}
   */
  async #importSwiper() {
    if (!ContentGalleryController.#swiperCache) {
      const [{ default: Swiper }, modules] = await Promise.all([
        import('swiper'),
        // eslint-disable-next-line import/no-unresolved
        import('swiper/modules')
      ])
      ContentGalleryController.#swiperCache = { Swiper, ...modules }
    }
    return ContentGalleryController.#swiperCache
  }
}
